import moment from 'moment';
import { safeJSONParse, utcMoment } from '../../../../../utils/common';
import { fCurrency } from '../../../../../utils/formatNumber';

export const getTotalSaleAmount = (data) => {
  return data?.reduce((acc, curr) => acc + curr?.pos_totalAmount, 0) || 0;
};
export const getTotalNumberOfSales = (data) => {
  return data?.length || 0;
};

export const getDiningCustomerOrderCount = (data) => {
  return data?.filter((value) => [1].includes(value?.pos_saleTypeId))?.length || 0;
};

export const getTakeAwayCustomerOrderCount = (data) => {
  return data?.filter((value) => [2].includes(value?.pos_saleTypeId))?.length || 0;
};

export const getPercentage = (total, value) => {
  return ((value / total) * 100).toFixed(2);
};

export const getAverageKotValues = (data, globalValue) => {
  const totals = {};
  const uniques = {};
  const specialCounts = {};
  const SPECIAL = '94770884019';

  (Array.isArray(data) ? data : []).forEach((v) => {
    if (![1, 2].includes(v?.pos_saleTypeId)) return;
    const loc = v?.pos_locationName ?? 'Unknown';
    const amt = Number(v?.pos_totalAmount) || 0;
    const phone = v?.customerPhone || '';

    totals[loc] = (totals[loc] || 0) + amt;

    if (phone) {
      if (phone === SPECIAL) {
        specialCounts[loc] = (specialCounts[loc] || 0) + 1;
      } else {
        if (!uniques[loc]) uniques[loc] = new Set();
        uniques[loc].add(phone);
      }
    }
  });

  return (
    <>
      <p>
        <span style={{ fontWeight: 800 }}>Global: </span>
        <span style={{ fontWeight: 500 }}>{fCurrency(globalValue)}</span>
      </p>
      {Object.keys(totals).map((loc) => {
        const uniqueCount = uniques[loc]?.size || 0;
        const special = specialCounts[loc] || 0;
        const denom = uniqueCount + special || 1;
        const avg = totals[loc] / denom;
        return (
          <p key={loc}>
            <span style={{ fontWeight: 800 }}>{loc}: </span>
            <span style={{ fontWeight: 500 }}>{fCurrency(avg)}</span>
          </p>
        );
      })}
    </>
  );
};

export const getWalkingCustomerAverageSpent = (data, globalValue, allLocations = [], selectedLocation = 'Global') => {
  const filteredData = data?.filter((value) => {
    return ['Dining', 'Take Away'].includes(value?.pos_saleTypeName) && !['94770884019'].includes(value?.customerPhone);
  });

  const averageValue = {};
  const customerListLocation = {};

  //  Initialize all POS-available locations (sorted)
  allLocations
    .filter((item) => item.isPosAvailable == 1)
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach((loc) => {
      averageValue[loc.name] = 0;
      customerListLocation[loc.name] = [];
    });

  // Populate values
  filteredData?.forEach((value) => {
    const location = value?.pos_locationName;
    if (!location || !averageValue.hasOwnProperty(location)) return;

    averageValue[location] += value?.pos_totalAmount;

    if (!customerListLocation[location].includes(value?.customerPhone)) {
      customerListLocation[location].push(value?.customerPhone);
    }
  });

  // Decide what locations to display
  const locationsToRender = selectedLocation.name === 'Global' ? Object.keys(averageValue) : [selectedLocation.name];

  return (
    <>
      <p>
        <span style={{ fontWeight: 800 }}>Global: </span>
        <span style={{ fontWeight: 500 }}>{fCurrency(globalValue)}</span>
      </p>

      {locationsToRender.map((key) => {
        const count = customerListLocation[key]?.length || 0;
        const avg = count > 0 ? averageValue[key] / count : 0;

        return (
          <p key={key}>
            <span style={{ fontWeight: 800 }}>{key}: </span>
            <span style={{ fontWeight: 500 }}>{fCurrency(avg)}</span>
          </p>
        );
      })}
    </>
  );
};

export const getAverageNewCustomerSale = (data, globalData, allLocations = [], selectedLocation) => {
  const uniquePhones = new Set();
  const filteredData = data?.filter((value) => {
    const phone = value?.customerPhone;
    if (phone && !uniquePhones.has(phone) && !['94770884019'].includes(phone)) {
      uniquePhones.add(phone);
      return [1, 2].includes(value?.pos_saleTypeId);
    }
    return false;
  });

  const uniquePhonesForGlobal = new Set();
  const filteredDataForGlobal =
    globalData
      ?.filter((value) => !utcMoment(value?.customerJoinDate).startOf('day').isBefore(utcMoment(value?.pos_dateTime).startOf('day')))
      ?.filter((value) => {
        const phone = value?.customerPhone;
        if (phone && !uniquePhonesForGlobal.has(phone) && !['94770884019'].includes(phone)) {
          uniquePhonesForGlobal.add(phone);
          return [1, 2].includes(value?.pos_saleTypeId);
        }
        return false;
      }) || [];

  const averageValue = {};
  const customerListLocation = {};

  // Initialize all POS-enabled locations (alphabetical)
  allLocations
    .filter((item) => item.isPosAvailable == 1)
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach((loc) => {
      averageValue[loc.name] = 0;
      customerListLocation[loc.name] = [];
    });

  // Overlay new-customer sales per location
  filteredData
    ?.filter((value) => !utcMoment(value?.customerJoinDate).startOf('day').isBefore(utcMoment(value?.pos_dateTime).startOf('day')))
    ?.forEach((value) => {
      const location = value?.pos_locationName;
      if (!location || !averageValue.hasOwnProperty(location)) return;

      averageValue[location] += value?.pos_totalAmount;

      if (!customerListLocation[location].includes(value?.customerPhone)) {
        customerListLocation[location].push(value?.customerPhone);
      }
    });

  //  Global calculation (unchanged, just guarded)
  const newCustomerCountForGlobal = filteredDataForGlobal?.filter((value) => !utcMoment(value?.customerJoinDate).startOf('day').isBefore(utcMoment(value?.pos_dateTime).startOf('day'))) || [];

  const globalSale = filteredDataForGlobal?.reduce((acc, curr) => acc + curr?.pos_totalAmount, 0) || 0;

  const globalAverage = newCustomerCountForGlobal.length > 0 ? globalSale / newCustomerCountForGlobal.length : 0;

  // Decide what to render
  const locationsToRender = selectedLocation.name === 'Global' ? Object.keys(averageValue) : [selectedLocation.name];

  return (
    <>
      <p>
        <span style={{ fontWeight: 800 }}>Global: </span>
        <span style={{ fontWeight: 500 }}>{fCurrency(globalAverage)}</span>
      </p>

      {locationsToRender.map((key) => {
        const count = customerListLocation[key]?.length || 0;
        const avg = count > 0 ? averageValue[key] / count : 0;

        return (
          <p key={key}>
            <span style={{ fontWeight: 800 }}>{key}: </span>
            <span style={{ fontWeight: 500 }}>{fCurrency(avg)}</span>
          </p>
        );
      })}
    </>
  );
};

export const getAverageReturningCustomerSale = (data, globalData, allLocations = [], selectedLocation) => {
  const uniquePhones = new Set();
  const filteredData = data?.filter((value) => {
    const phone = value?.customerPhone;
    if (phone && !uniquePhones.has(phone) && !['94770884019'].includes(phone)) {
      uniquePhones.add(phone);
      return [1, 2].includes(value?.pos_saleTypeId);
    }
    return false;
  });

  const uniquePhonesForGlobal = new Set();
  const filteredDataForGlobal =
    globalData
      ?.filter((value) => utcMoment(value?.customerJoinDate).startOf('day').isBefore(utcMoment(value?.pos_dateTime).startOf('day')))
      ?.filter((value) => {
        const phone = value?.customerPhone;
        if (phone && !uniquePhonesForGlobal.has(phone) && !['94770884019'].includes(phone)) {
          uniquePhonesForGlobal.add(phone);
          return [1, 2].includes(value?.pos_saleTypeId);
        }
        return false;
      }) || [];

  const averageValue = {};
  const customerListLocation = {};

  // Initialize all POS-enabled locations
  allLocations
    .filter((item) => item.isPosAvailable == 1)
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach((loc) => {
      averageValue[loc.name] = 0;
      customerListLocation[loc.name] = [];
    });
  console.log('allLocations ', allLocations);

  filteredData
    ?.filter((value) => utcMoment(value?.customerJoinDate).startOf('day').isBefore(utcMoment(value?.pos_dateTime).startOf('day')))
    ?.forEach((value) => {
      const location = value?.pos_locationName;
      if (!location || !averageValue.hasOwnProperty(location)) return;

      averageValue[location] += value?.pos_totalAmount;

      if (!customerListLocation[location].includes(value?.customerPhone)) {
        customerListLocation[location].push(value?.customerPhone);
      }
    });

  const locationsToRender = selectedLocation.name === 'Global' ? Object.keys(averageValue) : [selectedLocation.name];

  const returningCustomerCountForGlobal = filteredDataForGlobal?.filter((value) => utcMoment(value?.customerJoinDate).startOf('day').isBefore(utcMoment(value?.pos_dateTime).startOf('day'))) || [];

  const globalSale = filteredDataForGlobal?.reduce((acc, curr) => acc + curr?.pos_totalAmount, 0) || 0;

  const globalAverage = returningCustomerCountForGlobal.length > 0 ? globalSale / returningCustomerCountForGlobal.length : 0;

  return (
    <>
      <p>
        <span style={{ fontWeight: 800 }}>Global: </span>
        <span style={{ fontWeight: 500 }}>{fCurrency(globalAverage)}</span>
      </p>

      {locationsToRender.map((key) => {
        const count = customerListLocation[key]?.length || 0;
        const avg = count > 0 ? averageValue[key] / count : 0;

        return (
          <p key={key}>
            <span style={{ fontWeight: 800 }}>{key}: </span>
            <span style={{ fontWeight: 500 }}>{fCurrency(avg)}</span>
          </p>
        );
      })}
    </>
  );
};
export const getAverageReturningCustomerForLocation = (data, globalValue) => {
  const uniquePhones = new Set();
  const filteredData = data?.filter((value) => {
    const phone = value?.customerPhone;
    if (phone && !uniquePhones.has(phone) && !['94770884019'].includes(phone)) {
      uniquePhones.add(phone);
      return [1, 2].includes(value?.pos_saleTypeId);
    }
    return false;
  });
  const returningCustomerCount = {};
  const walkingCustomerCount = {};

  filteredData?.forEach((value) => {
    if (utcMoment(value?.customerJoinDate).startOf('day').isBefore(utcMoment(value?.pos_dateTime).startOf('day'))) {
      if (returningCustomerCount[value?.pos_locationName]) {
        returningCustomerCount[value?.pos_locationName] += 1;
      } else {
        returningCustomerCount[value?.pos_locationName] = 1;
      }
    }
    if (walkingCustomerCount[value?.pos_locationName]) {
      walkingCustomerCount[value?.pos_locationName] += 1;
    } else {
      walkingCustomerCount[value?.pos_locationName] = 1;
    }
  });
  return (
    <>
      <p>
        <span style={{ fontWeight: 800 }}>Global: </span>
        <span style={{ fontWeight: 500 }}>{globalValue}%</span>
      </p>
      {Object.keys(returningCustomerCount).map((key) => (
        <p key={key}>
          <span style={{ fontWeight: 800 }}>{key}: </span>
          <span style={{ fontWeight: 500 }}>{((returningCustomerCount[key] / walkingCustomerCount[key]) * 100).toFixed(2)}%</span>
        </p>
      ))}
    </>
  );
};

export const getReturnWalkingCustomerCount = (data) => {
  const uniquePhones = new Set();

  const filteredData = data?.filter((value) => {
    const phone = value?.customerPhone;
    if (phone && !uniquePhones.has(phone) && !['94770884019'].includes(phone)) {
      uniquePhones.add(phone);
      return [1, 2].includes(value?.pos_saleTypeId);
    }
    return false;
  });
  return (
    filteredData?.filter((value) => [1, 2].includes(value?.pos_saleTypeId) && utcMoment(value?.customerJoinDate).startOf('day').isBefore(utcMoment(value?.pos_dateTime).startOf('day')))?.length || 0
  );
};

export const getWalkingCustomerCount = (data) => {
  const uniquePhones = new Set();

  const filteredData = data?.filter((value) => {
    const phone = value?.customerPhone;
    if (phone && !uniquePhones.has(phone) && !['94770884019'].includes(phone)) {
      uniquePhones.add(phone);
      return [1, 2].includes(value?.pos_saleTypeId);
    }
    return false;
  });

  return filteredData?.length || 0;
};

export const getPickMeOrderCount = (data) => {
  return data?.filter((value) => [3].includes(value?.pos_saleTypeId))?.length || 0;
};

export const getUberOrderCount = (data) => {
  return data?.filter((value) => [4].includes(value?.pos_saleTypeId))?.length || 0;
};

const getDateRangeFromSalesData = (salesData) => {
  const sortedData = salesData.slice().sort((a, b) => new Date(a.pos_dateTime) - new Date(b.pos_dateTime));

  const startDate = moment.utc(sortedData[0]?.pos_dateTime);
  const endDate = moment.utc(sortedData[sortedData.length - 1]?.pos_dateTime);

  const daysDifference = Math.ceil(endDate.diff(startDate, 'hour') / 24);

  return { startDate, endDate, daysDifference };
};

export const getHourlySalesDateBased = (salesData) => {
  const { startDate, endDate } = getDateRangeFromSalesData(salesData);

  const totalHours = endDate.diff(startDate, 'hours');

  const hourlySales = Array.from({ length: totalHours + 1 }, (_, index) => ({
    dateTime: startDate.clone().add(index, 'hours').format('DD MMMM HH:00'),
    totalAmount: 0,
    totalSales: 0,
  }));

  salesData.forEach((sale) => {
    const saleDateTime = moment.utc(sale?.pos_dateTime);
    const hourIndex = saleDateTime.diff(startDate, 'hours');
    if (hourIndex >= 0 && hourIndex <= totalHours) {
      hourlySales[hourIndex].totalAmount += sale?.pos_totalAmount || 0;
      hourlySales[hourIndex].totalSales += 1;
    }
  });
  return {
    xAxis: hourlySales?.map((value) => value?.dateTime) || [],
    series: [
      {
        label: 'Total Amount',
        data: hourlySales?.map((value) => value?.totalAmount) || [],
      },
    ],
  };
};
export const getHourlySalesHourBased = (salesData) => {
  const hourlySales = Array.from({ length: 24 }, (_, index) => ({
    dateTime: index + 1,
    totalAmount: 0,
    totalSales: 0,
  }));

  salesData.forEach((sale) => {
    const hour = utcMoment(sale?.pos_dateTime).format('HH');
    if (hourlySales[hour]) {
      hourlySales[hour].totalAmount += sale?.pos_totalAmount || 0;
      hourlySales[hour].totalSales += 1;
    }
  });
  return {
    xAxis: hourlySales.map((value) => value.dateTime),
    series: [
      {
        name: 'Total Amount',
        data: hourlySales.map((value) => value.totalAmount),
      },
    ],
  };
};

export const getPaymentTypes = (data) => {
  const eachPaymentTypeAmounts = [];

  data?.forEach((value) => {
    value?.pos_payments?.forEach((payment) => {
      const paymentType = payment?.paymentTypesName;
      const paymentAmount = payment?.amount;
      const paymentIndex = eachPaymentTypeAmounts.findIndex((value) => value?.paymentType === paymentType);
      if (paymentIndex === -1) {
        eachPaymentTypeAmounts.push({
          paymentType,
          paymentAmount,
        });
      } else {
        eachPaymentTypeAmounts[paymentIndex].paymentAmount += paymentAmount;
      }
    });
  });
  return eachPaymentTypeAmounts?.map((data) => ({ value: data?.paymentAmount, label: data?.paymentType })) || [];
};

export const getPaymentTypeForValue = (data, key) => {
  const eachPaymentTypeAmounts = [];

  data?.forEach((value) => {
    value?.pos_payments?.forEach((payment) => {
      const paymentType = payment?.paymentTypesName;
      const paymentAmount = payment?.amount;
      const paymentIndex = eachPaymentTypeAmounts.findIndex((value) => value?.paymentType === paymentType);
      if (paymentIndex === -1) {
        eachPaymentTypeAmounts.push({
          paymentType,
          paymentAmount,
        });
      } else {
        eachPaymentTypeAmounts[paymentIndex].paymentAmount += paymentAmount;
      }
    });
  });
  return eachPaymentTypeAmounts?.find((data) => data?.paymentType === key)?.paymentAmount || 0;
};

export const getItemWiseSales = (data, type) => {
  const eachItemAmounts = [];
  data?.forEach((value) => {
    value?.pos_items?.forEach((item) => {
      const itemName = item?.foodName;
      const itemAmount = item?.qty;
      const itemIndex = eachItemAmounts.findIndex((value) => value?.itemName === itemName);
      if (itemIndex === -1) {
        eachItemAmounts.push({
          itemName,
          itemAmount,
        });
      } else {
        eachItemAmounts[itemIndex].itemAmount += itemAmount;
      }
    });
  });
  const sortedByValue = eachItemAmounts?.map((data) => ({ value: data?.itemAmount, label: data?.itemName }))?.sort((a, b) => b.value - a.value) || [];
  if (type === 'bar') {
    return {
      xAxis: [{ scaleType: 'band', data: sortedByValue?.map((value) => value?.label) || [] }],
      series: [
        {
          label: 'Total Count',
          data: sortedByValue?.map((value) => value?.value) || [],
        },
      ],
    };
  } else if (type === 'pie') {
    return sortedByValue?.map((value) => ({ value: value?.value, label: value?.label })) || [];
  } else if (type === 'table') {
    return sortedByValue?.map((value) => ({ Name: value?.label, qty: value?.value })) || [];
  }
};

export const getCustomerViseCounts = (data, type) => {
  const eachItemAmounts = [];
  data?.forEach((value) => {
    const itemName = `${value?.customerName}_${value?.customerPhone}`;
    const itemIndex = eachItemAmounts.findIndex((value) => value?.itemName === itemName);
    if (itemIndex === -1) {
      eachItemAmounts.push({
        itemName,
        itemAmount: 1,
      });
    } else {
      eachItemAmounts[itemIndex].itemAmount += 1;
    }
  });
  const sortedByValue = eachItemAmounts?.map((data) => ({ value: data?.itemAmount, label: data?.itemName }))?.sort((a, b) => b.value - a.value) || [];
  return (
    sortedByValue?.map((value) => ({
      Name: value?.label?.split('_')[0],
      Phone: value?.label?.split('_')[1],
      orderCount: value?.value,
    })) || []
  );
};

export const getVoucherWiseReport = (data) => {
  const voucherRows = [];

  data?.forEach((order) => {
    const voucherLogs = safeJSONParse(order?.pos_vouchers);
    const posPayments = safeJSONParse(order?.pos_payments);
    const voucherPayments = (Array.isArray(posPayments) ? posPayments : []).filter((payment) => {
      return payment?.paymentTypesId == 4;
    });
    const voucherLogRows = (Array.isArray(voucherLogs) ? voucherLogs : []).filter((voucher) => String(voucher?.promotionType || '').toLowerCase() === 'voucher');

    const orderCustomer = order?.customerName || '-';

    if (voucherLogRows.length > 0) {
      voucherLogRows.forEach((voucher) => {
        voucherRows.push({
          orderId: order?.pos_id,
          customer: orderCustomer,
          voucherName: voucher?.voucherName || voucher?.voucherCode || 'Voucher',
          amount: Number(voucher?.amount || 0),
        });
      });
      return;
    }

    voucherPayments.forEach((payment) => {
      voucherRows.push({
        orderId: order?.pos_id,
        customer: orderCustomer,
        voucherName: payment?.paymentTypesName || 'Voucher',
        amount: Number(payment?.amount || 0),
      });
    });
  });

  return voucherRows.sort((a, b) => Number(b.orderId || 0) - Number(a.orderId || 0));
};

export const getDiscountWiseReport = (data) => {
  const discountRows = [];

  data?.forEach((order) => {
    const promotionLogs = safeJSONParse(order?.pos_vouchers);
    const posPayments = safeJSONParse(order?.pos_payments);
    const discountPayments = (Array.isArray(posPayments) ? posPayments : []).filter((payment) => {
      return payment?.paymentTypesId == 2;
    });
    const discountLogRows = (Array.isArray(promotionLogs) ? promotionLogs : []).filter((promotion) => String(promotion?.promotionType || '').toLowerCase() === 'discount');

    const orderCustomer = order?.customerName || '-';

    if (discountLogRows.length > 0) {
      discountLogRows.forEach((discount) => {
        discountRows.push({
          orderId: order?.pos_id,
          customer: orderCustomer,
          discountName: discount?.voucherName || discount?.voucherCode || 'Discount',
          amount: Number(discount?.amount || 0),
        });
      });
      return;
    }

    discountPayments.forEach((payment) => {
      discountRows.push({
        orderId: order?.pos_id,
        customer: orderCustomer,
        discountName: payment?.paymentTypesName || 'Discount',
        amount: Number(payment?.amount || 0),
      });
    });
  });

  return discountRows.sort((a, b) => Number(b.orderId || 0) - Number(a.orderId || 0));
};

const removeDuplicates = (data) => {
  const uniquePhones = new Set();
  return data?.filter((item) => {
    if (item?.customerPhone && !uniquePhones.has(item.customerPhone)) {
      uniquePhones.add(item.customerPhone);
      return true;
    }
    return false;
  });
};
export const getAverageWalkingCustomerSaleSpentByCustomer = (data) => {
  const uniquePhones = new Set();
  const filteredData = data?.filter((value) => {
    const phone = value?.customerPhone;
    if (phone && !uniquePhones.has(phone) && !['94770884019'].includes(phone)) {
      uniquePhones.add(phone);
      return [1, 2].includes(value?.pos_saleTypeId);
    }
    return false;
  });
  const uniqueCustomerListCount = removeDuplicates(filteredData)?.length;
  const sumOfFilteredData = filteredData?.reduce((acc, curr) => acc + curr?.pos_totalAmount, 0) || 0;
  return sumOfFilteredData / uniqueCustomerListCount || 0;
};

export const getTotalSaleOfSaleType = (data, type) => {
  const filteredData = data?.filter((value) => {
    const phone = value?.customerPhone;
    if (phone) {
      return type.includes(value?.pos_saleTypeId);
    }
    return false;
  });
  const sumOfFilteredData = filteredData?.reduce((acc, curr) => acc + curr?.pos_totalAmount, 0) || 0;
  return sumOfFilteredData || 0;
};

export const getStatusCounts = (data, type) => {
  return data?.filter((item) => item.pos_status === type).length;
};

export const getReturningCustomerAmounts = (data, globalData, selectedLocationId, type) => {
  const uniquePhonesForGlobal = new Set();
  const filteredDataForGlobal =
    globalData
      ?.filter((value) => utcMoment(value?.customerJoinDate).startOf('day').isBefore(utcMoment(value?.pos_dateTime).startOf('day')))
      ?.filter((value) => {
        const phone = value?.customerPhone;
        // if (phone && !uniquePhonesForGlobal.has(phone) && !['94770884019'].includes(phone)) { // uniquePhonesForGlobal.has(phone) not needed
        if (phone && !['94770884019'].includes(phone)) {
          uniquePhonesForGlobal.add(phone);
          return type.includes(value?.pos_saleTypeId) && (Number(value?.pos_locationId) === Number(selectedLocationId) || selectedLocationId === 1);
        }
        return false;
      }) || [];
  const depositedSale = filteredDataForGlobal?.map((value) => {
    let depositSale = 0;
    const paymentData = safeJSONParse(value?.pos_payments);
    depositSale = paymentData?.map((payment) => {
      let depositAmount = 0;
      switch (payment.paymentTypesName) {
        case 'Cash':
          depositAmount = payment.amount;
          break;
        case 'Other Cards':
          depositAmount = payment.amount * 0.97;
          break;
        case 'Amex Cards':
          depositAmount = payment.amount * 0.97;
          break;
        case 'Pickme Food':
          depositAmount = payment.amount * 0.66;
          break;
        case 'Uber Eats':
          depositAmount = payment.amount * 0.66;
          break;
      }
      return {
        ...payment,
        depositAmount,
      };
    });
    return { ...value, depositedSale: depositSale?.reduce((acc, curr) => acc + curr?.depositAmount, 0) };
  });
  return depositedSale?.reduce((acc, curr) => acc + curr?.depositedSale, 0);
};

export const calculateKitchenPanelAverageTime = (kitchenPanelAverageTime) => {
  const toUtcDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    if (typeof v === 'string' && v.includes('T')) {
      const d = new Date(v);
      return isNaN(d.getTime()) ? null : d;
    }

    if (typeof v === 'string') {
      const m = v.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?$/);
      if (m) {
        const [, yyyy, MM, dd, hh, mm, ss] = m.map(Number);
        const ms = Date.UTC(yyyy, MM - 1, dd, hh, mm, ss, 0);
        const d = new Date(ms);
        return isNaN(d.getTime()) ? null : d;
      }
    }

    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };

  const diffMinutes = (start, end) => {
    if (!start || !end) return null;
    const ms = end.getTime() - start.getTime();
    return ms >= 0 ? ms / 60000 : null;
  };

  const parseItems = (posItems) => {
    if (!posItems) return [];
    if (Array.isArray(posItems)) return posItems;
    if (typeof posItems === 'string') {
      try {
        const arr = JSON.parse(posItems);
        return Array.isArray(arr) ? arr : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const average = (arr) => {
    if (!arr.length) return null;
    const sum = arr.reduce((a, b) => a + b, 0);
    return sum / arr.length;
  };

  const foodMins = [];
  const beverageMins = [];
  const orderMins = [];

  for (const row of Array.isArray(kitchenPanelAverageTime) ? kitchenPanelAverageTime : []) {
    const startAt = toUtcDate(row?.dateTime);

    const orderServed = toUtcDate(row?.servedTime);
    const orderDiff = diffMinutes(startAt, orderServed);
    if (orderDiff != null) {
      orderMins.push(orderDiff);
      // console.log(`[ORDER] POS #${row.id}: start=${startAt?.toISOString()} served=${orderServed?.toISOString()} diff=${orderDiff.toFixed(2)} min`);
    }

    const items = parseItems(row?.posItems);
    for (const it of items) {
      const qty = Number(it?.qty ?? 1) || 1;
      const itemServed = toUtcDate(it?.servedTime);
      const m = diffMinutes(startAt, itemServed);
      if (m == null) continue;

      const isBeverage = Number(it?.isBeverage ?? 0) === 1;
      const type = isBeverage ? 'BEVERAGE' : 'FOOD';
      // console.log(`[ITEM] POS #${row.id} ${type}: qty=${qty} start=${startAt?.toISOString()} served=${itemServed?.toISOString()} diff=${m.toFixed(2)} min`);

      for (let i = 0; i < qty; i++) {
        (isBeverage ? beverageMins : foodMins).push(m);
      }
    }
  }

  const fmt = (val) => (val == null ? '' : `${val.toFixed(1)} min`);

  const foodAvg = average(foodMins);
  const beverageAvg = average(beverageMins);
  const orderAvg = average(orderMins);

  return [
    { title: 'Food Preparation', value: fmt(foodAvg) },
    { title: 'Beverage Master', value: fmt(beverageAvg) },
    { title: 'Total Serving', value: fmt(orderAvg) },
  ];
};
